import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Chip, CircularProgress, Grid, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import ReceiptIcon from '@mui/icons-material/Receipt';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { getProfile } from 'utilities';

function fmt(amount, currency) {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: currency.toUpperCase() }).format(amount / 100);
}
function fmtDate(ts) {
  if (!ts) return '-';
  return new Date(ts * 1000).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}
function statusColor(status) {
  if (status === 'paid') return 'success';
  if (status === 'open') return 'warning';
  if (status === 'void' || status === 'uncollectible') return 'error';
  return 'default';
}

export default function InvoiceHistory({ embedded = false }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const profile = getProfile();
        if (!profile?.email) { setLoading(false); return; }
        const brand = window.location.hostname.includes('.co.uk') ? 'uk' : 'io';
        const res = await fetch('/api/stripe-invoices', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: profile.email, brand }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to load invoices');
        setInvoices(data.invoices || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const totalPaid = invoices.filter((i) => i.status === 'paid').reduce((s, i) => s + i.amountPaid, 0);
  const currency = invoices[0]?.currency || 'eur';

  return (
    <Box sx={{ width: '100%', p: embedded ? 3 : { xs: 1, md: 2 } }}>
      {!embedded && (
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#1f2937', mb: 3 }}>
          Invoice History
        </Typography>
      )}

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e5e7eb' }}>
            <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: '#dbeafe', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
              <ReceiptIcon sx={{ color: '#2563eb', fontSize: 22 }} />
            </Box>
            <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500 }}>Total Invoices</Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#1f2937', lineHeight: 1.2, mt: 0.5 }}>
              {loading ? <CircularProgress size={24} /> : invoices.length}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e5e7eb' }}>
            <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: '#d1fae5', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
              <ReceiptIcon sx={{ color: '#10b981', fontSize: 22 }} />
            </Box>
            <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500 }}>Total Paid</Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#1f2937', lineHeight: 1.2, mt: 0.5 }}>
              {loading ? <CircularProgress size={24} /> : fmt(totalPaid, currency)}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e5e7eb' }}>
            <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
              <ReceiptIcon sx={{ color: '#f59e0b', fontSize: 22 }} />
            </Box>
            <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500 }}>Open / Unpaid</Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#1f2937', lineHeight: 1.2, mt: 0.5 }}>
              {loading ? <CircularProgress size={24} /> : invoices.filter((i) => i.status === 'open').length}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e5e7eb' }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#1f2937', mb: 2 }}>All Invoices</Typography>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
        ) : invoices.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <ReceiptIcon sx={{ fontSize: 48, color: '#d1d5db', mb: 1 }} />
            <Typography color="text.secondary">No invoices found for this account.</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {['Invoice #', 'Date', 'Description', 'Amount', 'Status', 'Actions'].map((h) => (
                    <TableCell key={h} sx={{ fontWeight: 600, bgcolor: '#f9fafb', color: '#374151', border: 'none' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {invoices.map((inv) => (
                  <TableRow key={inv.id} hover sx={{ '&:last-child td': { border: 0 } }}>
                    <TableCell sx={{ color: '#6b7280', fontFamily: 'monospace' }}>{inv.number || inv.id.slice(-8)}</TableCell>
                    <TableCell sx={{ color: '#374151' }}>{fmtDate(inv.date)}</TableCell>
                    <TableCell sx={{ color: '#6b7280', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {inv.description || 'World Medical Card membership'}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#1f2937' }}>{fmt(inv.amountPaid || inv.amountDue, inv.currency)}</TableCell>
                    <TableCell>
                      <Chip label={inv.status} color={statusColor(inv.status)} size="small" sx={{ fontWeight: 600, textTransform: 'capitalize' }} />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {inv.pdfUrl && (
                          <a href={inv.pdfUrl} target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#2563eb', textDecoration: 'none', fontSize: 13, fontWeight: 600 }}>
                            <DownloadIcon sx={{ fontSize: 16 }} /> PDF
                          </a>
                        )}
                        {inv.hostedUrl && (
                          <a href={inv.hostedUrl} target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#6b7280', textDecoration: 'none', fontSize: 13 }}>
                            <OpenInNewIcon sx={{ fontSize: 16 }} /> View
                          </a>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
}
